/**
 * ck-facet-filters.js
 *
 * Refactor:
 * - Use event delegation (listeners survive Shopify AJAX rerenders).
 * - Keep dropdown logic exactly as before.
 * - Keep single-tag buttons working via facet:criteria-changed.
 * - Sync `is-active` state from URL after rerenders (MutationObserver, debounced).
 */

function getActiveTagsFromUrl() {
  return new URL(window.location.href).searchParams.getAll("filter.p.tag");
}

function syncSingleTagActiveState() {
  const activeTags = getActiveTagsFromUrl();
  document
    .querySelectorAll(".ck-filter-list__item_single_tag")
    .forEach((btn) => {
      const tagValue = btn.getAttribute("data-filter-tag");
      const shouldBeActive = !!tagValue && activeTags.includes(tagValue);
      btn.classList.toggle("is-active", shouldBeActive);
    });
}

function normalizeLabel(text) {
  return (text || "")
    .replace(":", "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function getDropdownLabelFromItem(item) {
  const toggle = item.querySelector(".ck-facet-toggle");
  if (!toggle) return null;
  // Toggle text often contains extra selected values after ":" (e.g. "Farbe: Nude, Pink").
  // We only want the base label ("Farbe") to match the top button label.
  const raw = toggle.textContent || "";
  const idx = raw.indexOf(":");
  const labelOnly = idx >= 0 ? raw.slice(0, idx) : raw;
  return normalizeLabel(labelOnly);
}

function getDropdownLabelFromButton(button) {
  return normalizeLabel(button.textContent);
}

function itemHasActiveSelection(item) {
  // Any checked filter input (checkbox/radio) inside this filter item
  if (item.querySelector('input[name^="filter."]:checked')) return true;

  // Price range: if any numeric input has a value
  const numberInputs = item.querySelectorAll(
    'input[name^="filter."][type="number"]'
  );
  for (const input of numberInputs) {
    if (input.value && String(input.value).trim() !== "") return true;
  }

  // Fallback: some themes store active values in hidden inputs
  const hiddenInputs = item.querySelectorAll(
    'input[name^="filter."][type="hidden"]'
  );
  for (const input of hiddenInputs) {
    if (input.value && String(input.value).trim() !== "") return true;
  }

  return false;
}

function syncDefaultDropdownState() {
  const topButtons = Array.from(
    document.querySelectorAll(".ck-filter-list__item_default")
  ).filter((btn) => !btn.classList.contains("ck-filter-list__item_single_tag"));

  const filterItems = Array.from(
    document.querySelectorAll(".product-facet__filter-item")
  );

  // Preserve "user-opened" dropdowns so they don't close immediately due to unrelated DOM mutations
  // (e.g. collapsible animations, ajax rerenders, etc.).
  const openLabels = new Set();
  for (const btn of topButtons) {
    if (btn.classList.contains("is-active")) {
      openLabels.add(getDropdownLabelFromButton(btn));
    }
  }
  for (const item of filterItems) {
    if (item.classList.contains("active")) {
      const label = getDropdownLabelFromItem(item);
      if (label) openLabels.add(label);
    }
  }

  // Reset states first, then re-apply (deterministic after AJAX rerender)
  for (const btn of topButtons) btn.classList.remove("is-active");
  for (const item of filterItems) {
    item.classList.remove("active");
    const toggle = item.querySelector(".ck-facet-toggle");
    if (toggle) toggle.classList.remove("is-active");
  }

  // Build quick lookup from label -> button
  const buttonByLabel = new Map();
  for (const btn of topButtons) {
    buttonByLabel.set(getDropdownLabelFromButton(btn), btn);
  }

  for (const item of filterItems) {
    const label = getDropdownLabelFromItem(item);
    if (!label) continue;

    const hasActive = itemHasActiveSelection(item);
    const shouldBeOpen = hasActive || openLabels.has(label);
    if (!shouldBeOpen) continue;

    // Ensure the corresponding top button is highlighted
    const btn = buttonByLabel.get(label);
    if (btn) btn.classList.add("is-active");

    // Ensure the dropdown container is visible (your custom "dropdown")
    item.classList.add("active");

    // Also highlight the main filter title inside the dropdown
    const toggle = item.querySelector(".ck-facet-toggle");
    if (toggle && hasActive) toggle.classList.add("is-active");
  }
}

let syncTimer = null;
function requestSync() {
  if (syncTimer) window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(() => {
    syncTimer = null;
    syncSingleTagActiveState();
    syncDefaultDropdownState();
  }, 50);
}

function dispatchFacetCriteriaChanged(url) {
  const facetFilters = document.getElementById("facet-filters");
  const productFacet = document.querySelector("product-facet");

  if (facetFilters && productFacet) {
    facetFilters.dispatchEvent(
      new CustomEvent("facet:criteria-changed", {
        detail: { url },
        bubbles: true,
      })
    );
    return true;
  }
  return false;
}

function handleSingleTagClick(button) {
  const tagValue = button.getAttribute("data-filter-tag");
  if (!tagValue) return;

  const isActive = button.classList.toggle("is-active");

  const newUrl = new URL(window.location.href);
  const currentTags = newUrl.searchParams.getAll("filter.p.tag");
  newUrl.searchParams.delete("filter.p.tag");

  if (isActive) {
    currentTags.forEach((tag) => {
      if (tag !== tagValue) newUrl.searchParams.append("filter.p.tag", tag);
    });
    newUrl.searchParams.append("filter.p.tag", tagValue);
  } else {
    currentTags.forEach((tag) => {
      if (tag !== tagValue) newUrl.searchParams.append("filter.p.tag", tag);
    });
  }

  // Keep sort_by if present (or any other params already in the URL).
  const dispatched = dispatchFacetCriteriaChanged(newUrl.toString());
  if (!dispatched) window.location.href = newUrl.toString();
}

function handleDropdownButtonClick(button) {
  const label = getDropdownLabelFromButton(button);
  const filterItems = document.querySelectorAll(".product-facet__filter-item");

  button.classList.toggle("is-active");

  filterItems.forEach((item) => {
    const itemLabel = getDropdownLabelFromItem(item);
    if (!itemLabel) return;
    if (itemLabel === label) item.classList.toggle("active");
  });
}

document.addEventListener("DOMContentLoaded", () => {
  // Initial sync for single-tag buttons on first load.
  syncSingleTagActiveState();
  syncDefaultDropdownState();

  // Keep active state correct for back/forward navigation.
  window.addEventListener("popstate", () => requestSync());

  // When Shopify rerenders filters/products via AJAX, the DOM changes.
  // We only need to resync button classes; event delegation continues to work.
  const observer = new MutationObserver(() => requestSync());
  const facetMain = document.getElementById("facet-main");
  const facetFilters = document.getElementById("facet-filters");
  if (facetMain)
    observer.observe(facetMain, { childList: true, subtree: true });
  if (facetFilters)
    observer.observe(facetFilters, { childList: true, subtree: true });
});

// Event delegation for all clicks on filter buttons (survives AJAX rerenders).
document.addEventListener("click", (e) => {
  const button = e.target.closest("button");
  if (!button) return;

  // Single tag buttons: filter products by tag.
  if (button.classList.contains("ck-filter-list__item_single_tag")) {
    e.preventDefault();
    e.stopPropagation();
    handleSingleTagClick(button);
    return;
  }

  // Dropdown buttons (the original working logic).
  if (button.classList.contains("ck-filter-list__item_default")) {
    handleDropdownButtonClick(button);
  }
});
